import { useState, useEffect } from "react";
import type { StudyMode } from "@/lib/supabase/types";

const STORAGE_KEY = "snapcards_study_mode";
const EVENT_NAME = "studymodechange";

export function getStudyMode(): StudyMode {
  if (typeof window === "undefined") return "flashcard";
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "mcq" ? "mcq" : "flashcard";
}

export function setStudyMode(mode: StudyMode): void {
  if (getStudyMode() === mode) return;
  localStorage.setItem(STORAGE_KEY, mode);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: mode }));
}

/** React hook — returns current mode and a setter. Stays in sync across components via custom event. */
export function useStudyMode(): [StudyMode, (m: StudyMode) => void] {
  const [mode, setModeState] = useState<StudyMode>("flashcard");

  useEffect(() => {
    setModeState(getStudyMode());
    const handler = (e: Event) => setModeState((e as CustomEvent).detail);
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, []);

  const update = (m: StudyMode) => {
    setModeState(m);
    setStudyMode(m);
  };

  return [mode, update];
}

/** Build study URL with mode param if MCQ */
export function studyUrl(deckId: string, mode?: StudyMode): string {
  const m = mode ?? getStudyMode();
  return m === "mcq" ? `/study/${deckId}?mode=mcq` : `/study/${deckId}`;
}

/** Build study/due URL with mode param if MCQ */
export function studyDueUrl(mode?: StudyMode): string {
  const m = mode ?? getStudyMode();
  return m === "mcq" ? "/study/due?mode=mcq" : "/study/due";
}
